import { OwnershipLabelsProvider } from "../kyc/OwnershipLabels";
import KycInitialDocumentsModal from "../kyc/sections/client/KycInitialDocumentsModal";
import { documentsRequestConfig, entityDocumentsTitle } from "../utils/documentsRequest";
import { clientEntityTypeOf, entityTypeComplete } from "../kyc/clientEntityType";
// KycClientSideFormPage.jsx
// ✅ Public external KYC form (NO direct Firestore access in browser)
// Route: /kyc-sent/:id?token=...

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { ref as storageRef, uploadBytesResumable } from "firebase/storage";
import logoMyKycGrey from "../images/mykyc-logo-grey-1000.png";
import logoMyKycLightblue3 from "../images/mykyc-logo-blue-800px.png";
import avatarIcon from "../images/avatar.svg";
import businessTradeIcon from "../images/businessTrade.svg";
import roiIcon from "../images/roi.svg";
import Lottie from "lottie-react";
import timerExpAnim from "../images/timer-exp-b.json";

import "./css/KycClientSideFormPage.css";
import "./css/ClientDocumentsUploadPage.css";

import { Toast } from "primereact/toast";
import { appConfirm } from "../components/ui/appConfirm";
import TopBand from "../components/ui/TopBand";
import { publicAuth as embeddedDocAuth, publicStorage as embeddedDocStorage } from "../firebase";

// ✅ Shared KYC sections (same as staff/review)
import KycSectionCustomerDetails from "../kyc/sections/common/KycSectionCustomerDetails";
// import KycSectionUaeCompany from "../kyc/sections/common/KycSectionUaeCompany";
import KycClientOwnershipDesktop from "../kyc/sections/client/KycClientOwnershipDesktop";
import KycClientSignatoriesDesktop from "../kyc/sections/client/KycClientSignatoriesDesktop";
import KycSectionSowSof from "../kyc/sections/common/KycSectionSowSof";
import KycSectionPepSanctions from "../kyc/sections/common/KycSectionPepSanctions";
import { buildLiveReviewOwnershipSnapshot } from "../kyc/sections/review/KycReviewOwnershipSection";
import { useLiveUboAssessment } from "../kyc/hooks/useLiveUboAssessment";
import {
  buildClientKycSaveData,
  pickClientEditable,
  toKycDateOrNull,
} from "../utils/clientKycPayload";
import {
  buildEmbeddedKycDocumentRequirements,
  embeddedMakeId,
  embeddedSafeStr,
  formatEmbeddedBytes,
  sanitizeEmbeddedFilename,
} from "../utils/embeddedKycDocuments";
import { KYC_SUBMISSION_API_BASE } from "../config/api";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

// Dedicated public client KYC submission API.
const API_BASE = KYC_SUBMISSION_API_BASE;
const DESKTOP_SUBMIT_PREVIEW_MODE = false;

function isValidEmail(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function hasRealDate(value) {
  return !!toKycDateOrNull(value);
}

function normalizePepSanctionsForClient(value) {
  const raw = value || {};
  const hasPepSelection = typeof raw.pepDeclared === "boolean";
  const hasSanctionsSelection = typeof raw.sanctionsDeclared === "boolean";

  return {
    ...raw,
    pepDeclared: hasPepSelection ? raw.pepDeclared : null,
    sanctionsDeclared: hasSanctionsSelection ? raw.sanctionsDeclared : null,
  };
}

function formatSubmittedAt(value) {
  if (!value || value === "Already submitted") return "";

  let dateValue = null;
  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value?.toDate === "function") {
    dateValue = value.toDate();
  } else if (typeof value === "object" && value.seconds) {
    dateValue = new Date(Number(value.seconds) * 1000);
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) dateValue = parsed;
  }

  if (!dateValue || Number.isNaN(dateValue.getTime())) return "";
  return dateValue.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function firstSubmittedAtText(...sources) {
  for (const source of sources) {
    const formatted = formatSubmittedAt(source);
    if (formatted) return formatted;
  }
  return "";
}

const PRE_LANDING_INTRO_WORDS = ["Secure", "onboarding,", "simplified"];
const PRE_LANDING_MS = 5900;

// -----------------------
// Helpers
// -----------------------
function lsKey(id, token, suffix) {
  const t = encodeURIComponent(token || "");
  return `kyc:${id}:${t}:${suffix}`;
}

function isCorporateKind(value) {
  const raw = String(value || "").trim().toLowerCase();
  return raw === "entity" || raw === "company" || raw === "corporate";
}

function normalizeOwnershipHolderType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "entity") return "entity";
  if (raw === "trust") return "trust";
  if (raw === "foundation") return "foundation";
  if (raw === "legalarrangement" || raw === "legal_arrangement") {
    return "legalArrangement";
  }
  return "individual";
}

function normalizeArrangementType(value) {
  return String(value || "").trim().toLowerCase() === "foundation"
    ? "Foundation"
    : "Trust";
}

function isCorporateArrangementParty(party) {
  const role = String(party?.role || "").trim().toLowerCase();
  const kind = party?.trusteeKind || party?.councilKind;
  return (
    isCorporateKind(kind) &&
    (role === "trustee" ||
      role === "protector" ||
      role === "council member" ||
      role === "guardian")
  );
}

function corporateArrangementPartyMatchesRow(row, party) {
  const rowName = String(row?.name || "").trim().toLowerCase();
  const partyName = String(party?.name || "").trim().toLowerCase();
  if (!rowName || rowName !== partyName) return false;

  const rowCountry = String(row?.countryOfResidence || "").trim().toLowerCase();
  const partyCountry = String(
    party?.domicile || party?.countryOfIncorporation || ""
  )
    .trim()
    .toLowerCase();
  return !rowCountry || !partyCountry || rowCountry === partyCountry;
}

function isCorporateArrangementControlRow(row, legalArrangements = []) {
  const basis = String(row?.basisOfControl || "").toLowerCase();
  const basisLooksCorporate =
    (basis.includes("trust — trustee") ||
      basis.includes("trust — protector") ||
      basis.includes("foundation — council/board") ||
      basis.includes("foundation — guardian")) &&
    (basis.includes("(corporate)") ||
      basis.includes("(company)") ||
      basis.includes("(entity)"));
  if (basisLooksCorporate) return true;

  return (Array.isArray(legalArrangements) ? legalArrangements : []).some((arr) =>
    (Array.isArray(arr?.parties) ? arr.parties : []).some(
      (party) =>
        isCorporateArrangementParty(party) &&
        corporateArrangementPartyMatchesRow(row, party)
    )
  );
}

function isUboRowComplete(row, legalArrangements = []) {
  if (!String(row?.name || "").trim()) return false;
  if (!String(row?.countryOfResidence || "").trim()) return false;
  if (isCorporateArrangementControlRow(row, legalArrangements)) return true;
  return !!String(row?.nationality || "").trim();
}

function isArrangementPartyComplete(party, arrangementType) {
  const role = String(party?.role || "").trim().toLowerCase();
  const name = String(party?.name || "").trim();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  const kind = String(party?.trusteeKind || party?.councilKind || "")
    .trim()
    .toLowerCase();
  if (!name) return false;

  const type = normalizeArrangementType(arrangementType);
  const canBeCorporate =
    (type === "Trust" && role === "trustee") ||
    (type === "Foundation" && role === "council member");
  if (canBeCorporate) {
    if (!["individual", "entity", "company", "corporate"].includes(kind)) {
      return false;
    }
    if (isCorporateKind(kind)) return !!domicile;
  }

  return !!nationality && !!domicile;
}

function findShareholderArrangement(shareholder, legalArrangements = []) {
  const rowId = String(shareholder?._rowId || "").trim();
  const nodeId = String(shareholder?.treeNodeId || shareholder?.subjectNodeId || "").trim();
  const name = String(shareholder?.name || "").trim().toLowerCase();
  return (Array.isArray(legalArrangements) ? legalArrangements : []).find((arr) => {
    if (String(arr?.source || "") !== "shareholder") return false;
    if (rowId && String(arr?.sourceRefRowId || "").trim() === rowId) return true;
    if (
      nodeId &&
      (String(arr?.sourceRefNodeId || "").trim() === nodeId ||
        String(arr?._treeArrangementNodeId || "").trim() === nodeId)
    ) {
      return true;
    }
    return (
      name &&
      String(arr?.name || "").trim().toLowerCase() === name
    );
  });
}

function isLegalArrangementShareholderComplete(shareholder, legalArrangements = []) {
  const arrangement = findShareholderArrangement(shareholder, legalArrangements);
  if (!arrangement) return false;
  const parties = Array.isArray(arrangement?.parties) ? arrangement.parties : [];
  const hasIdentity =
    !!String(arrangement?.name || shareholder?.name || "").trim() ||
    parties.some(
      (party) =>
        String(party?.role || "").trim() === "Trustee" &&
        !!String(party?.name || "").trim()
    );
  return (
    hasIdentity &&
    parties.length > 0 &&
    parties.every((party) =>
      isArrangementPartyComplete(party, arrangement?.arrangementType)
    )
  );
}

// ✅ safer JSON fetch (handles non-JSON responses too)
async function fetchJsonSafe(url, options) {
  const resp = await fetch(url, options);
  let rawText = "";
  let json = {};

  try {
    rawText = await resp.text();
    json = rawText ? JSON.parse(rawText) : {};
  } catch (e) {
    // ignore parse errors
  }

  return { resp, json, rawText };
}

function iso2ToFlagEmoji(iso2) {
  const cc = String(iso2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (cc.charCodeAt(0) - 65),
    A + (cc.charCodeAt(1) - 65)
  );
}

function stripMetaInTitle(title) {
  return String(title || "")
    .replace(/\s*\(Nationality:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s*\(Domicile:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s*\(Country:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseRichLines(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return [];
  return raw
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((line) => {
      const text = line.startsWith("•") ? line.slice(1).trim() : line;
      const lower = text.toLowerCase();
      const isHeading =
        lower === "required:" ||
        lower.startsWith("provide at least one of:") ||
        /^provide at least \d+ of:/.test(lower) ||
        lower.startsWith("also provide (optional):");
      const isNote = /^\(.+\)$/.test(text);
      return { type: isHeading ? "heading" : isNote ? "note" : "bullet", text };
    });
}

function isConditionalEntityRequirement(req, lines) {
  if (req?.meta?.kind !== "entity") return false;
  const hasConditionNote = (lines || []).some(
    (line) => line.type === "note" && /depend|applicable|mainland|free zone/i.test(String(line.text || ""))
  );
  const bulletCount = (lines || []).filter((line) => line.type === "bullet").length;
  return hasConditionNote && bulletCount > 1;
}

function displayRequirementHeading(req, lines, headingText, headingIndex) {
  const text = String(headingText || "");
  if (text.toLowerCase() !== "required:") return text;

  if (isConditionalEntityRequirement(req, lines)) return "Provide at least 1 of:";

  let count = 0;
  for (let i = headingIndex + 1; i < lines.length; i++) {
    if (lines[i].type === "heading") break;
    if (lines[i].type === "bullet") count++;
  }
  return count === 1 ? "Provide the following:" : "Provide all of the following:";
}

function requirementLinesForDisplay(req, lines) {
  const indexedLines = (lines || []).map((line, index) => ({ ...line, _displayIndex: index }));
  if (!isConditionalEntityRequirement(req, indexedLines)) return indexedLines;
  return [
    ...indexedLines.filter((line) => line.type === "heading"),
    ...indexedLines.filter((line) => line.type === "bullet"),
    ...indexedLines.filter((line) => line.type === "note"),
  ];
}

function renderRequirementNoteText(req, lines, text) {
  const value = String(text || "").trim();
  if (!isConditionalEntityRequirement(req, lines)) return value;
  const unwrapped = value.replace(/^\((.*)\)$/s, "$1").trim();
  return `Note: ${unwrapped}`;
}

function getLineFlag({ req, line }) {
  const kind = req?.meta?.kind;
  const text = String(line || "").toLowerCase();
  if (kind === "individual") {
    if (text.includes("passport")) return iso2ToFlagEmoji(req?.meta?.nationalityIso2);
    if (
      text.includes("proof of address") ||
      text.includes("proof of adress") ||
      text.includes("utility bill") ||
      text.includes("poa")
    ) {
      return iso2ToFlagEmoji(req?.meta?.domicileIso2);
    }
  }
  return "";
}

function getPartyIcon(kind) {
  if (kind === "entity") return businessTradeIcon;
  if (kind === "individual") return avatarIcon;
  return roiIcon;
}

function getPartyPill(kind) {
  if (kind === "entity") return "Entity";
  if (kind === "individual") return "Individual";
  return "";
}

// Deep-equal helpers (for optional changed highlight)
function isPlainObject(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}
function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }

  if (isPlainObject(a) || isPlainObject(b)) {
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
      if (!deepEqual(a[k], b[k])) return false;
    }
    return true;
  }

  return false;
}

function getAt(obj, path) {
  let ref = obj;
  for (const p of path) {
    if (ref == null) return undefined;
    ref = ref[p];
  }
  return ref;
}

function useIsMobileClientForm() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);

    setIsMobile(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }

    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isMobile;
}

export default function KycClientSideFormPage() {
  const { id } = useParams();
  const location = useLocation();
  const isMobile = useIsMobileClientForm();
  return isMobile ? <Navigate to={`/kyc-sent-mobile/${id}${location.search || ""}`} replace /> : <KycClientSideDesktopForm />;
}

function KycClientSideDesktopForm() {
  const reduceIntakeMotion = useReducedMotion();
  const { id } = useParams(); // ✅ this is the kyc/{id} document id
  const location = useLocation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";



  const toastRef = useRef(null);
  const embeddedDocFileInputRef = useRef(null);
  const embeddedAutoUploadRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // split errors:
  // loadErr = fatal (invalid link) => hide form
  // err = non-fatal (save failure) => keep form visible
  const [loadErr, setLoadErr] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [separateNames, setSeparateNames] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState(""); // entity | individual
  const [orgJurisdiction, setOrgJurisdiction] = useState(null);
  const [intakeFirstCompleted, setIntakeFirstCompleted] = useState(false);
  // Org setting: documents uploaded at the start / at the end of the form, for individuals or entities.
  const [documentsRequest, setDocumentsRequest] = useState(() => documentsRequestConfig(null));
  const [corporateDocuments, setCorporateDocuments] = useState(null);
  const [useMainCountryOfOperation, setUseMainCountryOfOperation] = useState(false);
  const typeLower = String(clientType || "").toLowerCase();

  const [submitted, setSubmitted] = useState(false);
  const [submittedAtText, setSubmittedAtText] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [toastBackdropVisible, setToastBackdropVisible] = useState(false);
  const toastBackdropTimerRef = useRef(null);
  const [showPreLanding, setShowPreLanding] = useState(true);
  const [embeddedDocChoice, setEmbeddedDocChoice] = useState("");
  const [embeddedDocSession, setEmbeddedDocSession] = useState(null);
  const [embeddedDocItems, setEmbeddedDocItems] = useState([]);
  const [embeddedDocActiveCardId, setEmbeddedDocActiveCardId] = useState("");
  const [embeddedDocDragOverCardId, setEmbeddedDocDragOverCardId] = useState("");
  const [embeddedDocBusy, setEmbeddedDocBusy] = useState(false);
  const [embeddedDocAttempted, setEmbeddedDocAttempted] = useState(false);
  const [embeddedDocError, setEmbeddedDocError] = useState("");

  // ✅ full structure kept for backend compatibility (even if not displayed)
  const [form, setForm] = useState({
    // not displayed here, but kept for compatibility
    engagement: {
      clientRequests: [],
      approach: {
        channel: "Direct",
        introducerName: "",
        paidIntroducer: false,
      },
    },

    customer: {
      type: "individual",
      name: "",
      nationalityOrIncorporationCountry: "",
      mainCountryOfOperation: "",
      dobOrIncorporationDate: null,
      email: "",
      phone: "",
      address: "",
      city: "",
      country: "",
      website: "",
      businessActivity: "",
    },

    uaeCompany: {
      proposedEntityNames: "",
      jurisdiction: "Mainland",
      activities: "",
      expectedMonthlyTurnoverRange: "",
      expectedCustomerCountries: [],
      expectedSupplierCountries: [],
      expectedTransactionChannels: [],
      highRiskCountries: [],
    },

    ownership: {
      shareholders: [],
      ubos: [],
      controllers: [],
    },

    sourceOfFundsWealth: {
      sourceOfFunds: "",
      sourceOfFundsDetails: "",
      sourceOfWealth: [],
      sourceOfWealthDetails: "",
      supportingDocuments: [],
      netWorthCurrency: "USD",
      netWorthRange: "",
      annualIncomeCurrency: "USD",
      annualIncomeRange: "",
      annualTurnoverCurrency: "USD",
      annualTurnoverRange: "",
      sourceOfFundsTypes: [],
      sourceOfFundsLoanDetails: "",
      sourceOfFundsOtherDetails: "",
      paymentRoutes: [],
      paymentRoutesThirdPartyDetails: "",
      paymentRoutesOtherDetails: "",
    },

    pepSanctions: {
      pepDeclared: null,
      pepDetails: "",
      sanctionsDeclared: null,
      sanctionsDetails: "",
    },

    // not displayed here, but kept for compatibility
    documents: {
      individualDocs: [],
      corporateDocs: [],
      missingDocs: [],
      notes: "",
    },

    // not displayed here, but kept for compatibility
    internalUse: {
      riskRating: "Medium",
      eddRequired: false,
      eddRationale: "",
      screeningSummary: "",
      goAmlFiled: false,
      goAmlReference: "",
    },

    // Comment Sent to staff for review
    clientComment: "",
  });

  const customerType = form?.customer?.type || "individual";
  const isEntity = customerType === "entity";
  // "none" | "start" | "end" for this client (individual or entity).
  const documentsTiming = isEntity ? documentsRequest.entities : documentsRequest.individuals;
  const shouldShowEmbeddedDocuments = documentsTiming === "end";
  const incorporationCountry = isEntity
    ? String(form?.customer?.nationalityOrIncorporationCountry || "").trim().toUpperCase()
    : "";
  // Entity: the registration documents of its country of incorporation (names shown to the client).
  useEffect(() => {
    if (!isEntity || documentsTiming === "none" || !/^[A-Z]{2}$/.test(incorporationCountry) || !id || !token) {
      setCorporateDocuments(null);
      return undefined;
    }
    let alive = true;
    const url = `${API_BASE}/myKyc/clients-side/kyc/corporate-documents?clientId=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}&country=${encodeURIComponent(incorporationCountry)}`;
    // Unavailable list: general guidance only (fallback), never a blocked upload.
    const unavailable = { country: incorporationCountry, fallback: true, unavailable: true, cases: [] };
    fetchJsonSafe(url)
      .then(({ resp, json }) => { if (alive) setCorporateDocuments(resp.ok && json?.ok ? json : unavailable); })
      .catch(() => { if (alive) setCorporateDocuments(unavailable); });
    return () => { alive = false; };
  }, [isEntity, documentsTiming, incorporationCountry, id, token]);
  const customer = form?.customer || {};
  const sow = form?.sourceOfFundsWealth || {};
  const pep = form?.pepSanctions || {};
  const customerValid =
    String(customer.name || "").trim() &&
    String(customer.nationalityOrIncorporationCountry || "").trim() &&
    (!isEntity ||
      !useMainCountryOfOperation ||
      String(customer.mainCountryOfOperation || "").trim()) &&
    hasRealDate(customer.dobOrIncorporationDate) &&
    String(customer.address || "").trim() &&
    String(customer.city || "").trim() &&
    String(customer.country || "").trim() &&
    isValidEmail(customer.email || "") &&
    String(customer.phone || "").trim() &&
    String(customer.businessActivity || "").trim();
  const selectedSowTypes = Array.isArray(sow.sourceOfWealth)
    ? sow.sourceOfWealth
    : [];
  const selectedSofTypes = Array.isArray(sow.sourceOfFundsTypes)
    ? sow.sourceOfFundsTypes
    : [];
  const selectedPaymentRoutes = Array.isArray(sow.paymentRoutes)
    ? sow.paymentRoutes
    : [];
  const ownership = form?.ownership || {};
  const ownershipProfile = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const ownershipHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const requiresCompanyTurnover =
    isEntity &&
    (ownershipProfile === "operating" ||
      (ownershipProfile === "holding" && ownershipHoldingKind === "active"));
  const wealthValid =
    selectedSowTypes.length > 0 &&
    selectedSofTypes.length > 0 &&
    selectedPaymentRoutes.length > 0 &&
    String(sow.netWorthRange || "").trim() &&
    String(sow.annualIncomeRange || "").trim() &&
    (!requiresCompanyTurnover || String(sow.annualTurnoverRange || "").trim()) &&
    (!selectedSowTypes.includes("other") ||
      String(sow.sourceOfWealthDetails || "").trim()) &&
    (!selectedSofTypes.includes("loan") ||
      String(sow.sourceOfFundsLoanDetails || "").trim()) &&
    (!selectedSofTypes.includes("other") ||
      String(sow.sourceOfFundsOtherDetails || "").trim()) &&
    (!selectedPaymentRoutes.includes("third_party_payment") ||
      String(sow.paymentRoutesThirdPartyDetails || "").trim()) &&
    (!selectedPaymentRoutes.includes("other") ||
      String(sow.paymentRoutesOtherDetails || "").trim());
  const pepValid =
    typeof pep.pepDeclared === "boolean" &&
    typeof pep.sanctionsDeclared === "boolean";
  const ownershipCompletionMethod = String(ownership?.desktopClientCompletionMethod || "").trim();
  const ownershipTerminalProfile =
    ownershipProfile === "quoted" ||
    ownershipProfile === "trust" ||
    ownershipProfile === "foundation";
  const ownershipValid =
    !isEntity ||
    (!!ownershipProfile &&
      (ownershipProfile !== "holding" || !!ownershipHoldingKind) &&
      (ownershipTerminalProfile || !!ownershipCompletionMethod));
  const signatoryValid =
    !isEntity ||
    (Array.isArray(form?.ownership?.signatories) &&
      form.ownership.signatories.some(
        (r) =>
          String(r?.name || "").trim() &&
          String(r?.capacity || "").trim() &&
          String(r?.nationality || "").trim() &&
          String(r?.countryOfResidence || "").trim()
      ));

  const ownershipRowsValid = !isEntity || (() => {
    const shareholders = Array.isArray(form?.ownership?.shareholders) ? form.ownership.shareholders : [];
    const ubos = Array.isArray(form?.ownership?.ubos) ? form.ownership.ubos : [];
    const legalArrangements = Array.isArray(form?.ownership?.legalArrangements?.arrangements)
      ? form.ownership.legalArrangements.arrangements
      : [];
    const shareholdersOk = shareholders.every((r) => {
      if (!String(r?.name || "").trim()) return false;
      const t = String(r?.holderType || "").toLowerCase();
      const holderType = normalizeOwnershipHolderType(r?.holderType);
      if (holderType === "trust" || holderType === "foundation" || holderType === "legalArrangement") {
        return isLegalArrangementShareholderComplete(r, legalArrangements);
      }
      if (!String(r?.countryOfResidence || "").trim()) return false;
      if (t === "individual" && !String(r?.nationality || "").trim()) return false;
      return true;
    });
    const ubosOk = ubos.every((row) => isUboRowComplete(row, legalArrangements));
    return shareholdersOk && ubosOk;
  })();

  const desktopClientFormComplete = customerValid && wealthValid && pepValid && ownershipValid && ownershipRowsValid && signatoryValid;
  const embeddedDocRequirements = useMemo(
    () =>
      shouldShowEmbeddedDocuments
        ? buildEmbeddedKycDocumentRequirements(form, clientName || "Client", undefined, corporateDocuments)
        : [],
    [form, clientName, shouldShowEmbeddedDocuments, corporateDocuments]
  );
  const shouldRenderEmbeddedDocuments =
    shouldShowEmbeddedDocuments && embeddedDocRequirements.length > 0;
  const embeddedDoneCardIds = useMemo(
    () =>
      new Set(
        embeddedDocItems
          .filter((item) => item.status === "done" && item.objectPath)
          .map((item) => embeddedSafeStr(item.cardId))
      ),
    [embeddedDocItems]
  );
  const embeddedQueuedCount = useMemo(
    () => embeddedDocItems.filter((item) => item.status === "queued").length,
    [embeddedDocItems]
  );
  const embeddedGroupedRequirements = useMemo(() => {
    const map = new Map();
    for (const req of embeddedDocRequirements || []) {
      const kind = embeddedSafeStr(req?.meta?.kind);
      const partyName = embeddedSafeStr(req?.meta?.partyName);
      const partyRole = embeddedSafeStr(req?.meta?.partyRole);
      const groupKey =
        kind === "individual" || kind === "entity"
          ? `party::${kind}::${
              embeddedSafeStr(req?.meta?.partyId) ||
              partyName ||
              embeddedSafeStr(req?.cardId)
            }`
          : `other::${kind || "misc"}::${
              embeddedSafeStr(req?.meta?.sowKey) || embeddedSafeStr(req?.cardId)
            }`;

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          kind,
          partyName,
          partyRole,
          requirements: [],
        });
      }
      map.get(groupKey).requirements.push(req);
    }
    return Array.from(map.values());
  }, [embeddedDocRequirements]);
  const isEmbeddedDocumentsComplete =
    !shouldRenderEmbeddedDocuments ||
    embeddedDocChoice === "later" ||
    (embeddedDocChoice === "upload_now" &&
      embeddedQueuedCount === 0 &&
      embeddedDocRequirements.every((req) =>
        embeddedDoneCardIds.has(embeddedSafeStr(req.cardId))
      ));
  const isDesktopSubmitComplete =
    desktopClientFormComplete && isEmbeddedDocumentsComplete;

  // Everything that still blocks submission, by section (same rules as the checks above), so the
  // client sees all the missing details at once.
  function missingSubmitDetails() {
    const out = [];
    const add = (section, text) => out.push({ section, text });
    const blank = (v) => !String(v ?? "").trim();
    const S = { customer: "Client details", own: "Ownership", sig: "Authorised signatories", sow: "Source of funds & wealth", pep: "PEP & sanctions", docs: "Documents" };
    if (blank(customer.name)) add(S.customer, isEntity ? "Company name" : "Full name");
    if (blank(customer.nationalityOrIncorporationCountry)) add(S.customer, isEntity ? "Country of incorporation" : "Nationality");
    if (isEntity && useMainCountryOfOperation && blank(customer.mainCountryOfOperation)) add(S.customer, "Main country of operation");
    if (!hasRealDate(customer.dobOrIncorporationDate)) add(S.customer, isEntity ? "Date of incorporation" : "Date of birth");
    if (blank(customer.address)) add(S.customer, "Address");
    if (blank(customer.city)) add(S.customer, "City");
    if (blank(customer.country)) add(S.customer, "Country of the address");
    if (!isValidEmail(customer.email || "")) add(S.customer, "A valid email address");
    if (blank(customer.phone)) add(S.customer, "Phone number");
    if (blank(customer.businessActivity)) add(S.customer, "Business activity");

    if (isEntity) {
      if (!ownershipProfile) add(S.own, "Type of company");
      else if (ownershipProfile === "holding" && !ownershipHoldingKind) add(S.own, "Active or passive holding");
      else if (!ownershipTerminalProfile && !ownershipCompletionMethod) add(S.own, "How you want to enter the owners");
      const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements) ? ownership.legalArrangements.arrangements : [];
      (Array.isArray(ownership?.shareholders) ? ownership.shareholders : []).forEach((r, i) => {
        const name = String(r?.name || "").trim();
        const label = name || `Shareholder ${i + 1}`;
        const type = normalizeOwnershipHolderType(r?.holderType);
        if (!name) { add(S.own, `${label}: name`); return; }
        if (type === "trust" || type === "foundation" || type === "legalArrangement") {
          if (!isLegalArrangementShareholderComplete(r, arrangements)) add(S.own, `${label}: ${type === "foundation" ? "foundation" : "trust"} details`);
          return;
        }
        const missing = [
          String(r?.holderType || "").toLowerCase() === "individual" && blank(r?.nationality) ? "nationality" : "",
          blank(r?.countryOfResidence) ? (String(r?.holderType || "").toLowerCase() === "individual" ? "domicile" : "country of incorporation") : "",
        ].filter(Boolean);
        if (missing.length) add(S.own, `${label}: ${missing.join(", ")}`);
      });
      (Array.isArray(ownership?.ubos) ? ownership.ubos : []).forEach((r, i) => {
        if (isUboRowComplete(r, arrangements)) return;
        const name = String(r?.name || "").trim();
        const missing = [!name ? "name" : "", blank(r?.nationality) ? "nationality" : "", blank(r?.countryOfResidence) ? "domicile" : ""].filter(Boolean);
        add(S.own, `${name || `Controlling person ${i + 1}`}: ${missing.join(", ") || "details"}`);
      });

      if (!signatoryValid) {
        const signatories = (Array.isArray(ownership?.signatories) ? ownership.signatories : []).filter((r) => String(r?.name || "").trim());
        if (!signatories.length) add(S.sig, "At least one authorised signatory");
        signatories.forEach((r) => {
          const missing = [blank(r?.capacity) ? "capacity" : "", blank(r?.nationality) ? "nationality" : "", blank(r?.countryOfResidence) ? "domicile" : ""].filter(Boolean);
          if (missing.length) add(S.sig, `${String(r.name).trim()}: ${missing.join(", ")}`);
        });
      }
    }

    if (!selectedSowTypes.length) add(S.sow, "Source of wealth");
    if (selectedSowTypes.includes("other") && blank(sow.sourceOfWealthDetails)) add(S.sow, "Details of the other source of wealth");
    if (blank(sow.netWorthRange)) add(S.sow, "Net worth");
    if (blank(sow.annualIncomeRange)) add(S.sow, "Annual income");
    if (requiresCompanyTurnover && blank(sow.annualTurnoverRange)) add(S.sow, "Annual turnover");
    if (!selectedSofTypes.length) add(S.sow, "Source of funds");
    if (selectedSofTypes.includes("loan") && blank(sow.sourceOfFundsLoanDetails)) add(S.sow, "Loan details");
    if (selectedSofTypes.includes("other") && blank(sow.sourceOfFundsOtherDetails)) add(S.sow, "Details of the other source of funds");
    if (!selectedPaymentRoutes.length) add(S.sow, "Payment routes");
    if (selectedPaymentRoutes.includes("third_party_payment") && blank(sow.paymentRoutesThirdPartyDetails)) add(S.sow, "Third-party payment details");
    if (selectedPaymentRoutes.includes("other") && blank(sow.paymentRoutesOtherDetails)) add(S.sow, "Details of the other payment route");

    if (typeof pep.pepDeclared !== "boolean") add(S.pep, "Politically exposed person (PEP) declaration");
    if (typeof pep.sanctionsDeclared !== "boolean") add(S.pep, "Sanctions declaration");

    if (shouldRenderEmbeddedDocuments) {
      if (!embeddedDocChoice) add(S.docs, "Upload the documents now or provide them later");
      else if (embeddedDocChoice === "upload_now" && embeddedQueuedCount > 0) add(S.docs, "Upload the selected files");
      else if (embeddedDocChoice === "upload_now" && !isEmbeddedDocumentsComplete) add(S.docs, "One file for each requested document");
    }
    return out;
  }
  // ✅ dynamic section numbering (Ownership exists only for entities)
  const sectionNo = useMemo(() => {
    let n = 1;
    const out = {};
    out.customerDetails = n++; // 1
    out.ownership = isEntity ? n++ : null; // 2 (only if entity)
    out.signatories = isEntity ? n++ : null; // 3 (only if entity)
    out.sowSof = n++;
    out.sanctionsPep = n++;
    out.documents = shouldRenderEmbeddedDocuments ? n++ : null;
    out.comments = n++;
    return out;
  }, [isEntity, shouldRenderEmbeddedDocuments]);

  // ✅ shared setter used by your modular sections
  // Re-run the UBO assessment after ownership edits, so the submitted results are never outdated.
  useLiveUboAssessment({ form, setForm, orgJurisdiction, enabled: !loading });

  function setAt(path, value) {
    setMsg("");
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  }

  // -------------------------------------------------------
  // ✅ Load via Node API: verify token + header + draft
  // -------------------------------------------------------
  useEffect(() => {
    let alive = true;

    async function run() {
      try {
        setLoadErr("");
        setErr("");
        setMsg("");
        setLoading(true);

        if (!id || !token) {
          setLoadErr(
            "This link is missing required parameters (id/token). Please request a new secure link."
          );
          return;
        }

        // If already submitted on THIS browser, show thank-you directly
        const alreadySubmitted =
          !DESKTOP_SUBMIT_PREVIEW_MODE &&
          window.localStorage.getItem(lsKey(id, token, "submitted")) === "1";

        if (alreadySubmitted) {
          if (!alive) return;
          setSubmitted(true);
          setSubmittedAtText(
            firstSubmittedAtText(
              window.localStorage.getItem(lsKey(id, token, "submittedAt"))
            )
          );
          setClientName(
            window.localStorage.getItem(lsKey(id, token, "clientName")) || ""
          );
          setClientType(
            window.localStorage.getItem(lsKey(id, token, "clientType")) || ""
          );
          return;
        }

        // 1) Verify token
        const verifyUrl = `${API_BASE}/myKyc/clients-side/kyc/verify-link?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;

        const {
          resp: vResp,
          json: vj,
          rawText: vRaw,
        } = await fetchJsonSafe(verifyUrl);

        if (!vResp.ok || !vj.ok) {
          setLoadErr(
            vj?.error ||
              vRaw ||
              "Link invalid or expired. Please request a new link."
          );
          return;
        }

        // If already used, show thank-you screen
        if (vj.used) {
          if (!alive) return;

          // Best-effort load header for nice display
          const headerUrl = `${API_BASE}/myKyc/clients-side/kyc/client-header?clientId=${encodeURIComponent(
            id
          )}&token=${encodeURIComponent(token)}`;

          const { resp: hResp, json: hj } = await fetchJsonSafe(headerUrl);
          if (hResp.ok && hj.ok) {
            setClientName(hj.clientName || "");
            setSeparateNames(hj.nameEntryMode === "separate");
            setClientType(hj.clientTypeValue || hj.clientType || "");
            setDocumentsRequest(hj.documentsRequest || documentsRequestConfig({ documentsRequest: { mode: hj.documentsRequestMode } }));
            setOrgJurisdiction(hj.orgGeo ?? null);
          }

          setSubmitted(true);
          setSubmittedAtText(
            firstSubmittedAtText(
              vj.submittedAt,
              hj?.submittedAt
            )
          );
          return;
        }

        // 2) Load client header
        const headerUrl = `${API_BASE}/myKyc/clients-side/kyc/client-header?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;

        const {
          resp: hResp,
          json: hj,
          rawText: hRaw,
        } = await fetchJsonSafe(headerUrl);

        if (!hResp.ok || !hj.ok) {
          setLoadErr(hj?.error || hRaw || "Failed to load client information.");
          return;
        }

        if (!alive) return;

        setClientName(hj.clientName || "");
            setSeparateNames(hj.nameEntryMode === "separate");
        setClientType(hj.clientTypeValue || hj.clientType || "");
        setDocumentsRequest(hj.documentsRequest || documentsRequestConfig({ documentsRequest: { mode: hj.documentsRequestMode } }));
        setOrgJurisdiction(hj.orgGeo ?? null);
        setUseMainCountryOfOperation(
          hj.useMainCountryOfOperation === true ||
            hj.countryRisk?.useMainCountryOfOperation === true
        );

        // lock customer.type based on header
        const headerType = String(
          hj.clientTypeValue || hj.clientType || ""
        ).toLowerCase();

        const lockedType = headerType === "entity" ? "entity" : "individual";

        setForm((prev) => ({
          ...prev,
          customer: {
            ...prev.customer,
            type: lockedType,
            email: hj.contactEmail || prev.customer.email,
            phone: hj.contactPhone || prev.customer.phone,
          },
        }));

        // 3) Load existing saved draft (if any)
        const draftUrl = `${API_BASE}/myKyc/clients-side/kyc/draft?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;

        const {
          resp: dResp,
          json: dj,
          rawText: dRaw,
        } = await fetchJsonSafe(draftUrl);

        if (!dResp.ok || !dj.ok) {
          setLoadErr(dj?.error || dRaw || "Failed to load your saved draft.");
          return;
        }

        if (!alive) return;

        setIntakeFirstCompleted(dj.intakeFirstCompleted === true);
        if (dj.data) {
          const data = dj.data;
          const loadedCustomer = data.customer || {};

          // merge draft into state, but keep locked type
          setForm((prev) => ({
            ...prev,
            ...data,
            engagement: { ...prev.engagement, ...(data.engagement || {}) },
            customer: {
              ...prev.customer,
              ...loadedCustomer,
              type: lockedType, // ✅ always locked
              dobOrIncorporationDate: toKycDateOrNull(
                loadedCustomer.dobOrIncorporationDate
              ),
            },
            uaeCompany: { ...prev.uaeCompany, ...(data.uaeCompany || {}) },
            ownership: { ...prev.ownership, ...(data.ownership || {}) },
            // sourceOfFundsWealth: {
            //   ...prev.sourceOfFundsWealth,
            //   ...(data.sourceOfFundsWealth || {}),
            //   sourceOfWealth: Array.isArray(
            //     data.sourceOfFundsWealth?.sourceOfWealth
            //   )
            //     ? data.sourceOfFundsWealth.sourceOfWealth
            //     : [],
            // },
            sourceOfFundsWealth: {
              ...prev.sourceOfFundsWealth,
              ...(data.sourceOfFundsWealth || {}),

              // ✅ always store as array (SoW checkboxes)
              sourceOfWealth: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfWealth
              )
                ? data.sourceOfFundsWealth.sourceOfWealth
                : [],

              // ✅ always store as array (SoF MultiSelect)
              sourceOfFundsTypes: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfFundsTypes
              )
                ? data.sourceOfFundsWealth.sourceOfFundsTypes
                : [],
            },
            // sourceOfFundsWealth: {
            //   ...prev.sourceOfFundsWealth,
            //   ...(data.sourceOfFundsWealth || {}),
            // },
            pepSanctions: {
              ...prev.pepSanctions,
              ...normalizePepSanctionsForClient(data.pepSanctions),
            },
            documents: { ...prev.documents, ...(data.documents || {}) },
            internalUse: { ...prev.internalUse, ...(data.internalUse || {}) },
          }));
        }
      } catch (e) {
        setLoadErr(e?.message || "Unexpected error while loading.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id, token]);

  // -------------------------------------------------------
  // ✅ Save / Submit via Node API
  // -------------------------------------------------------

  // SAVING PROCESS
  // 1.	Client-side: apiSave() builds payload.data (currently basically all form)
  // 2.	Server-side KYC save handler: decides what to store, where to store it, and whether to merge/overwrite/whitelist
  // 3.	Firestore: write happens only in the server code

  function formWithLatestOwnershipGraph(sourceForm) {
    const nextForm =
      sourceForm && typeof sourceForm === "object" ? sourceForm : {};
    const ownership =
      nextForm.ownership && typeof nextForm.ownership === "object"
        ? nextForm.ownership
        : {};
    const accountHolderName =
      String(nextForm.customer?.name || "").trim() || "Account holder";
    const liveOwnershipGraph = buildLiveReviewOwnershipSnapshot({
      ownership,
      accountHolderName,
    });

    if (!liveOwnershipGraph?.mermaid) return nextForm;

    return {
      ...nextForm,
      ownership: {
        ...ownership,
        assistantTree: liveOwnershipGraph,
      },
    };
  }

  useEffect(() => {
    if (embeddedAutoUploadRef.current) return;
    const hasQueued = embeddedDocItems.some((item) => item.status === "queued");
    if (!hasQueued) return;
    embeddedAutoUploadRef.current = true;
    uploadEmbeddedDocuments().finally(() => {
      embeddedAutoUploadRef.current = false;
    });
  }, [embeddedDocItems, embeddedDocBusy]); // eslint-disable-line

  function openEmbeddedDocPicker(cardId) {
    if (saving || embeddedDocBusy) return;
    setEmbeddedDocActiveCardId(embeddedSafeStr(cardId));
    embeddedDocFileInputRef.current?.click?.();
  }

  function addEmbeddedDocFiles(fileList, cardId) {
    const cid = embeddedSafeStr(cardId);
    if (!cid) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setEmbeddedDocItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: embeddedMakeId(),
        cardId: cid,
        file,
        name: file.name || "file",
        size: file.size || 0,
        type: file.type || "application/octet-stream",
        progress: 0,
        status: "queued",
        error: "",
        objectPath: "",
      })),
    ]);
  }

  function onEmbeddedDocDragOver(event, cardId) {
    event.preventDefault();
    event.stopPropagation();
    if (saving || embeddedDocBusy) return;
    setEmbeddedDocDragOverCardId(embeddedSafeStr(cardId));
  }

  function onEmbeddedDocDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setEmbeddedDocDragOverCardId("");
  }

  function onEmbeddedDocDrop(event, cardId) {
    event.preventDefault();
    event.stopPropagation();
    if (saving || embeddedDocBusy) return;
    setEmbeddedDocDragOverCardId("");
    addEmbeddedDocFiles(event.dataTransfer?.files, cardId);
  }

  function removeEmbeddedDocItem(itemId) {
    setEmbeddedDocItems((prev) => prev.filter((item) => item.id !== itemId));
  }

  async function ensureEmbeddedDocSession() {
    if (embeddedDocSession?.uploadPrefix) return embeddedDocSession;

    const { resp, json, rawText } = await fetchJsonSafe(
      `${API_BASE}/myKyc/clients-side/kyc/embedded-doc-upload/auth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: id, token }),
      }
    );

    if (!resp.ok || json?.ok !== true) {
      throw new Error(json?.error || rawText || "Failed to start document upload.");
    }

    const firebaseToken = embeddedSafeStr(json.firebaseToken);
    if (!firebaseToken) throw new Error("Missing upload token.");

    try {
      await signOut(embeddedDocAuth);
    } catch {}
    const cred = await signInWithCustomToken(embeddedDocAuth, firebaseToken);
    await cred.user.getIdToken(true);

    const nextSession = {
      sessionId: embeddedSafeStr(json.sessionId),
      requestId: embeddedSafeStr(json.requestId),
      uploadPrefix: embeddedSafeStr(json.uploadPrefix),
      maxFileBytes: Number(json.maxFileBytes || 25 * 1024 * 1024),
    };
    setEmbeddedDocSession(nextSession);
    return nextSession;
  }

  async function uploadEmbeddedDocuments() {
    const queued = embeddedDocItems.filter((item) => item.status === "queued");
    if (!queued.length) return { ok: true, uploadedCardIds: new Set() };

    setEmbeddedDocBusy(true);
    setEmbeddedDocError("");

    try {
      const session = await ensureEmbeddedDocSession();
      const maxBytes = Number(session.maxFileBytes || 25 * 1024 * 1024);
      const uploadedCardIds = new Set();
      let hasFailedUpload = false;

      for (const item of queued) {
        if (!item.file) continue;
        if (Number(item.size || 0) > maxBytes) {
          hasFailedUpload = true;
          setEmbeddedDocItems((prev) =>
            prev.map((x) =>
              x.id === item.id
                ? { ...x, status: "error", error: "File is too large." }
                : x
            )
          );
          continue;
        }

        await new Promise((resolve) => {
          const objectPath = `${session.uploadPrefix}/${embeddedSafeStr(
            item.cardId || "card"
          )}/${Date.now()}_${sanitizeEmbeddedFilename(item.name)}`;
          const ref = storageRef(embeddedDocStorage, objectPath);

          setEmbeddedDocItems((prev) =>
            prev.map((x) =>
              x.id === item.id
                ? { ...x, status: "uploading", progress: 1, objectPath }
                : x
            )
          );

          const task = uploadBytesResumable(ref, item.file, {
            contentType: item.type,
          });

          task.on(
            "state_changed",
            (snap) => {
              const pct = snap.totalBytes
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id ? { ...x, progress: pct } : x
                )
              );
            },
            (error) => {
              hasFailedUpload = true;
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id
                    ? {
                        ...x,
                        status: "error",
                        progress: 0,
                        error: error?.message || "Upload failed",
                      }
                    : x
                )
              );
              resolve();
            },
            () => {
              uploadedCardIds.add(embeddedSafeStr(item.cardId));
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id
                    ? { ...x, status: "done", progress: 100 }
                    : x
                )
              );
              resolve();
            }
          );
        });
      }

      return { ok: !hasFailedUpload, uploadedCardIds };
    } catch (error) {
      const message = error?.message || "Document upload failed.";
      setEmbeddedDocError(message);
      showValidationToast(message);
      return { ok: false, uploadedCardIds: new Set() };
    } finally {
      setEmbeddedDocBusy(false);
    }
  }

  function buildEmbeddedDocumentsPayload() {
    if (!shouldRenderEmbeddedDocuments) return null;
    if (embeddedDocChoice === "later") return { choice: "later" };
    if (embeddedDocChoice !== "upload_now") return { choice: "" };

    const requirementByCard = new Map(
      embeddedDocRequirements.map((req) => [embeddedSafeStr(req.cardId), req])
    );
    const files = embeddedDocItems
      .filter((item) => item.status === "done" && item.objectPath)
      .map((item) => {
        const cardId = embeddedSafeStr(item.cardId);
        const req = requirementByCard.get(cardId);
        return {
          objectPath: item.objectPath,
          originalName: item.name,
          contentType: item.type,
          sizeBytes: item.size,
          cardId,
          cardTitle: embeddedSafeStr(req?.title) || null,
        };
      });

    return {
      choice: "upload_now",
      sessionId: embeddedDocSession?.sessionId || null,
      requestId: embeddedDocSession?.requestId || null,
      uploadPrefix: embeddedDocSession?.uploadPrefix || null,
      files,
    };
  }

  async function apiSave(submit) {
    const formToSave = formWithLatestOwnershipGraph(form);
    const payload = {
      clientId: id,
      token,
      // ✅ send full structure (backend can merge)
      // data: {
      //   ...form,
      //   ...pickClientEditable(form),
      // },
      data: buildClientKycSaveData(formToSave, separateNames ? "separate" : "full"), // ✅ same sanitized payload as mobile
      embeddedDocuments: buildEmbeddedDocumentsPayload(),
      intakeFirstCompleted,
      submit: !!submit,
    };

    const { resp, json, rawText } = await fetchJsonSafe(
      `${API_BASE}/myKyc/clients-side/kyc/save`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!resp.ok || !json.ok) {
      throw new Error(json?.error || rawText || "Save failed");
    }
    return json;
  }

  async function handleSaveDraft() {
    try {
      if (saving) return;
      setErr("");
      setMsg("");
      setSaving(true);

      await apiSave(false);

      setMsg(
        "Saved ✅ You can return later using the same link (until it expires)."
      );

      toastRef.current?.show({
        severity: "success",
        summary: "Draft saved",
        detail: "Saved ✅",
        life: 2500,
      });
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function doSubmitFinal() {
    try {
      if (saving) return;
      setErr("");
      setMsg("");
      setSaving(true);

      const now = new Date();

      if (!DESKTOP_SUBMIT_PREVIEW_MODE) {
        await apiSave(true);

        window.localStorage.setItem(lsKey(id, token, "submitted"), "1");
        window.localStorage.setItem(
          lsKey(id, token, "submittedAt"),
          now.toISOString()
        );
        window.localStorage.setItem(
          lsKey(id, token, "clientName"),
          clientName || ""
        );
        window.localStorage.setItem(
          lsKey(id, token, "clientType"),
          clientType || ""
        );
      }

      setSubmitted(true);
      setSubmittedAtText(formatSubmittedAt(now));
    } catch (e) {
      setErr(e?.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  }

  function showValidationToast(detail, life = 2600) {
    setToastBackdropVisible(true);
    window.clearTimeout(toastBackdropTimerRef.current);
    toastBackdropTimerRef.current = window.setTimeout(() => {
      setToastBackdropVisible(false);
    }, life);

    toastRef.current?.show({
      severity: "secondary",
      summary: "A few details are still missing",
      detail,
      life,
    });
  }

  // A click outside the message closes it (message and blurred background fade out).
  function closeValidationToast() {
    window.clearTimeout(toastBackdropTimerRef.current);
    toastRef.current?.clear();
    setToastBackdropVisible(false);
  }

  // All missing details, grouped by section (the toast stays longer when the list is long).
  function showMissingDetailsToast(items) {
    const sections = [];
    items.forEach((item) => {
      const group = sections.find((g) => g.section === item.section);
      if (group) group.items.push(item.text);
      else sections.push({ section: item.section, items: [item.text] });
    });
    showValidationToast(
      <div className="kycMissingDetails">
        <div className="kycMissingDetailsIntro">
          Please complete {items.length === 1 ? "this item" : `these ${items.length} items`} before submitting.
        </div>
        {sections.map((group) => (
          <div key={group.section} className="kycMissingDetailsGroup">
            <div className="kycMissingDetailsHead">
              <span>{group.section}</span>
              <span className="kycMissingDetailsCount">{group.items.length}</span>
            </div>
            <div className="kycMissingDetailsChips">
              {group.items.map((text) => <span key={text} className="kycMissingDetailsChip">{text}</span>)}
            </div>
          </div>
        ))}
      </div>,
      Math.min(12000, 4000 + items.length * 700)
    );
  }

  function handleSubmit() {
    if (saving) return;
    if (!isDesktopSubmitComplete) {
      setSubmitAttempted(true);
      if (shouldRenderEmbeddedDocuments && !isEmbeddedDocumentsComplete) setEmbeddedDocAttempted(true);
      const missing = missingSubmitDetails();
      if (missing.length) {
        showMissingDetailsToast(missing);
        return;
      }
      if (!ownershipValid) {
        showValidationToast("Please complete the ownership and control section before submitting.");
      } else if (!ownershipRowsValid) {
        showValidationToast("Please complete all ownership rows (name, nationality, domicile are required).");
      } else if (!signatoryValid) {
        showValidationToast("Please add at least one authorised signatory before submitting.");
      } else if (shouldRenderEmbeddedDocuments && !embeddedDocChoice) {
        setEmbeddedDocAttempted(true);
        showValidationToast("Choose whether to upload documents now or provide them later.");
      } else if (
        shouldRenderEmbeddedDocuments &&
        embeddedDocChoice === "upload_now" &&
        embeddedQueuedCount > 0
      ) {
        setEmbeddedDocAttempted(true);
        showValidationToast("Upload selected files before submitting.");
      } else if (
        shouldRenderEmbeddedDocuments &&
        embeddedDocChoice === "upload_now" &&
        !isEmbeddedDocumentsComplete
      ) {
        setEmbeddedDocAttempted(true);
        showValidationToast("Upload one file for each requested document card before submitting.");
      } else {
        showValidationToast("Complete all required fields before submitting.");
      }
      return;
    }

    appConfirm({
      group: "appConfirm",
      header: "Confirm submission",
      message:
        "After submission, this link will stop working. Contact us if you need changes.",
      defaultFocus: "reject",
      acceptLabel: "Submit",
      rejectLabel: "Cancel",
      scheme: "beige",
      icon: "bi bi-send",
      accept: () => doSubmitFinal(),
    });
  }

  // -------------------------------------------------------
  // Changed highlight support (optional)
  // -------------------------------------------------------
  const baseEditable = useMemo(() => pickClientEditable({}), []);
  const currentEditable = useMemo(() => pickClientEditable(form), [form]);

  function isChanged(path) {
    const a = getAt(baseEditable, path);
    const b = getAt(currentEditable, path);
    return !deepEqual(a, b);
  }
  function changedClass(path) {
    return isChanged(path) ? "kycChanged" : "";
  }

  useEffect(() => {
    if (!showPreLanding) return;

    const timer = window.setTimeout(() => {
      setShowPreLanding(false);
    }, PRE_LANDING_MS);

    return () => window.clearTimeout(timer);
  }, [showPreLanding]);

  function handlePreLandingWordAnimationEnd() {
    setShowPreLanding((prev) => (prev ? false : prev));
  }

  function renderDesktopPreLanding() {
    return (
      <AnimatePresence>
        {showPreLanding ? (
          <motion.div
            className="kycDesktopPreLanding"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.14,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
          >
            <div className="kycDesktopPreLandingLogo" aria-hidden="true">
              <img src={logoMyKycGrey} alt="" />
            </div>
            <div className="kycDesktopPreLandingWordWrap">
              <div className="kycDesktopPreLandingIntro">
                <span className="kycDesktopPreLandingIntroText">
                  {PRE_LANDING_INTRO_WORDS.map((word, index) => (
                    <span
                      key={`${word}-${index}`}
                      className="kycDesktopPreLandingIntroWord"
                      style={{ "--word-index": index }}
                    >
                      {word}
                    </span>
                  ))}
                </span>
              </div>
              <div
                className="kycDesktopPreLandingWord"
                onAnimationEnd={handlePreLandingWordAnimationEnd}
              >
                <span className="kycDesktopPreLandingWordToken">Know</span>
                <br />
                <span className="kycDesktopPreLandingWordToken">Your</span>{" "}
                <span className="kycDesktopPreLandingWordToken">Customer</span>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  }

  const showInitialDocumentsCard = !loading && !loadErr && !submitted && !showPreLanding &&
    documentsTiming === "start" && !intakeFirstCompleted;
  // Upload-first company: once Step 1 is done, the sections after Ownership & control stay muted until it is
  // completed (type and method chosen, and for a non-terminal type the shareholders entered and complete).
  const ownershipHasNamedShareholders = (Array.isArray(form?.ownership?.shareholders) ? form.ownership.shareholders : [])
    .some((row) => String(row?.name || "").trim());
  const ownershipStepDone = ownershipValid && (ownershipTerminalProfile || (ownershipHasNamedShareholders && ownershipRowsValid));
  const awaitingOwnership = isEntity && documentsTiming === "start" && intakeFirstCompleted && !showInitialDocumentsCard &&
    !submitted && !ownershipStepDone;

  // -------------------------------------------------------
  // Renders
  // -------------------------------------------------------
  return (
    <OwnershipLabelsProvider jurisdiction={orgJurisdiction}>
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="KycClientSideFormPage"
    >
      {renderDesktopPreLanding()}


      {loadErr ? (
        <div className="kycPageContainer pb-4">
          <div className="kycDesktopLinkErrorWrap">
            <motion.div
              className="kycDesktopLinkErrorHeader"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="kycDesktopLinkErrorBadge">Secure Form · Link expired</div>

              <Lottie
                animationData={timerExpAnim}
                loop={true}
                lottieRef={(ref) => ref?.setSpeed(0.5)}
                className="kycDesktopLinkErrorLottie"
                aria-hidden="true"
              />

              <motion.h1
                className="kycDesktopLinkErrorTitle"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                This link can’t be used
              </motion.h1>

              <motion.p
                className="kycDesktopLinkErrorLead"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                {loadErr}
              </motion.p>
            </motion.div>

            <motion.div
              className="kycDesktopLinkErrorCard"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="kycDesktopLinkErrorCardTitle">What to do next</div>
              <div className="kycDesktopLinkErrorCardItem">
                <span className="kycDesktopLinkErrorCardDot" aria-hidden="true" />
                <span>Please request a new secure link from the staff team.</span>
              </div>
              <div className="kycDesktopLinkErrorCardItem">
                <span className="kycDesktopLinkErrorCardDot" aria-hidden="true" />
                <span>If you believe this is an error, you can try refreshing the page.</span>
              </div>

              <div className="kycDesktopLinkErrorActions">
                <button
                  type="button"
                  className="btn kycBtnOutline"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            </motion.div>

            <motion.div
              className="kycSentFooterBrand"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
            >
              <img src={logoMyKycGrey} alt="myKYC" className="kycSentFooterLogo kycSentFooterLogoError" />
            </motion.div>
          </div>
        </div>
      ) : submitted ? (
        <div className="kycPageContainer pb-4">
          <div className="kycDesktopSubmitWrap">
            <motion.div
              className="kycDesktopSubmitHeader"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="kycDesktopSubmitBadge">
                Thank you for your submission
              </div>

              <div className="kycDesktopSubmitOrb" aria-hidden="true">
                <motion.div
                  className="kycDesktopSubmitOrbPulse"
                  initial={{ scale: 0.78, opacity: 0.2 }}
                  animate={{ scale: 1.16, opacity: 0.52 }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    repeatType: "mirror",
                    ease: "easeInOut",
                  }}
                />
                <motion.div
                  className="kycDesktopSubmitOrbCore"
                  initial={{ scale: 0.7, rotate: -16, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <i className="bi bi-check2" />
                </motion.div>
              </div>

              <motion.h1
                className="kycDesktopSubmitTitle"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                KYC information received
              </motion.h1>

              {clientName ? (
                <motion.div
                  className="kycDesktopSubmitClientBadge"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.38, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {clientName}
                </motion.div>
              ) : null}

              <motion.p
                className="kycDesktopSubmitLead"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                Your submission has been securely delivered to our team.
              </motion.p>

              {submittedAtText ? (
                <motion.div
                  className="kycDesktopSubmitTimestamp"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.34, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  Submitted on {submittedAtText}
                </motion.div>
              ) : null}
            </motion.div>

            <motion.div
              className="kycDesktopSubmitNextCard"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="kycDesktopSubmitNextTitle">What happens next</div>
              <div className="kycDesktopSubmitNextItem">
                <span className="kycDesktopSubmitNextDot" aria-hidden="true" />
                <span>
                  Our Compliance team will review the submission and revert if
                  necessary.
                </span>
              </div>
              <div className="kycDesktopSubmitNextItem">
                <span className="kycDesktopSubmitNextDot" aria-hidden="true" />
                <span>
                  You may also be asked to provide supporting documentation to
                  complete the review.
                </span>
              </div>
            </motion.div>

            <motion.div
              className="kycSentFooterBrand"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
            >
              <img src={logoMyKycLightblue3} alt="myKYC" className="kycSentFooterLogo kycSentFooterLogoSubmit" />
            </motion.div>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`kycDesktopToastBackdrop${toastBackdropVisible ? " isVisible" : ""}`}
            onMouseDown={toastBackdropVisible ? closeValidationToast : undefined}
            aria-hidden="true"
          />
	          <TopBand
	            title="Know Your Customer (KYC)"
	            contentClassName="kycPartClientInfo"
	            smallBandColor="#8fa8bb"
	            titleColor="#ffffff"
	            subtitle={
	              clientName ? (
	                <div className="d-flex align-items-center flex-wrap gap-2">
	                  <span>{clientName}</span>
	                  {clientType && (
	                    <span
	                      className={
	                        "kycTypeBadge " +
	                        (typeLower === "entity"
	                          ? "kycTypeBadgeEntity"
	                          : "kycTypeBadgeIndividual")
	                      }
	                    >
	                      {typeLower === "entity" ? "Entity" : "Individual"}
	                    </span>
	                  )}
	                </div>
	              ) : (
	                "Please complete the required fields and submit."
	              )
	            }
	          />

      <div className="kycPageContainer pb-4">
        <input
          ref={embeddedDocFileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(event) => {
            addEmbeddedDocFiles(event.target.files, embeddedDocActiveCardId);
            event.target.value = "";
          }}
        />

        <div className="card kycCard kycIntroCard">
          <div className="kycSectionBody" style={{ userSelect: "none" }}>
            <div className="kycIntroHeading">
              Why we ask for this information
            </div>
            <div className="text-muted">
              This Know Your Customer (KYC) form is required to meet UAE
              regulatory and compliance obligations. Please complete it
              accurately and ensure the information is up to date.
            </div>
            <div className="text-muted" style={{ marginTop: 7, display: "none" }}>
              If you are unsure about any questions, you may leave it blank and
              add a note in the comments section at the end so you can clarify
              it with us.
            </div>
            <p
              style={{ marginTop: 10 }}
              className="text-muted small legend-intro-text"
            >
              Your information will be handled confidentially and used only for
              compliance purposes.
            </p>
          </div>
        </div>

        {/* non-fatal errors */}
        {err && <div className="alert alert-danger">{err}</div>}
        {msg && <div className="alert alert-success">{msg}</div>}

      <AnimatePresence initial={false}>
        {showInitialDocumentsCard && (
          <motion.div key={id + token} initial={false}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0, pointerEvents: "none" }}
            transition={{
              height: { duration: reduceIntakeMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: reduceIntakeMotion ? 0 : 0.3 },
            }}
            style={{ overflow: "hidden" }}>
            <KycInitialDocumentsModal inline clientId={id} token={token}
              form={form} separateNames={separateNames} setForm={setForm}
              includeCv={!isEntity && documentsRequest.individualsCv}
              entity={isEntity} corporateDocuments={corporateDocuments} entityDocTypes={documentsRequest.entitiesDocTypes}
              onComplete={() => setIntakeFirstCompleted(true)} />
          </motion.div>
        )}
      </AnimatePresence>

        <div className={`kycInitialDocsFollowing${showInitialDocumentsCard ? " is-awaiting-intake" : ""}`}
          inert={showInitialDocumentsCard} aria-disabled={showInitialDocumentsCard || undefined}>
        {/* ✅ Modular sections (no engagement/doc/internal sections displayed) */}
        <KycSectionCustomerDetails
          separateNames={separateNames}
          sectionNo={sectionNo.customerDetails}
          form={form}
          mode="client"
          onSetAt={setAt}
          submitAttempted={submitAttempted}
          useMainCountryOfOperation={useMainCountryOfOperation}
          // changedClass={changedClass}
          // readOnly={true}
        />

        {isEntity ? (
          <KycClientOwnershipDesktop
            entityTypeAnsweredEarlier={isEntity && documentsTiming === "start" && entityTypeComplete(clientEntityTypeOf(form?.ownership))}
            structurePromptPaused={showInitialDocumentsCard}
            separateNames={separateNames}
            orgJurisdiction={orgJurisdiction}
            sectionNo={sectionNo.ownership}
            form={form}
            onSetAt={setAt}
            changedClass={changedClass}
            readOnly={false}
            submitAttempted={submitAttempted}
            clientFacing
          />
        ) : null}

        {/* Upload-first (company): the sections after Ownership & control stay muted until it is completed. */}
        <div className={`kycAfterOwnership${awaitingOwnership ? " is-awaiting-ownership" : ""}`}
          inert={awaitingOwnership} aria-disabled={awaitingOwnership || undefined}>
        {isEntity ? (
          <KycClientSignatoriesDesktop
            separateNames={separateNames}
            sectionNo={sectionNo.signatories}
            form={form}
            onSetAt={setAt}
            changedClass={changedClass}
            readOnly={false}
            submitAttempted={submitAttempted}
          />
        ) : null}

        {/* <KycSectionOwnership
          form={form}
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={false}
        /> */}

        {/* Optional SOW/SOF section */}
        <KycSectionSowSof
          orgJurisdiction={orgJurisdiction}
          sectionNo={sectionNo.sowSof}
          form={form}
          mode="client"
          onSetAt={setAt}
          submitAttempted={submitAttempted}
          changedClass={changedClass}
          readOnly={false}
        />
        {/* <KycSectionSowSof
          form={form}
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={false}
        /> */}

        <KycSectionPepSanctions
          sectionNo={sectionNo.sanctionsPep}
          form={form}
          mode="client"
          onSetAt={setAt}
          submitAttempted={submitAttempted}
          changedClass={changedClass}
          readOnly={false}
        />

        {/* <KycSectionPepSanctions
          form={form}
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={false}
        /> */}

        {shouldRenderEmbeddedDocuments ? (
          <div className="card kycCard mb-3">
            <div className="kycSectionHead">
              <h5 className="kycSectionTitle">
                {sectionNo.documents} | {isEntity ? entityDocumentsTitle(documentsRequest) : "Identification Documents"}
              </h5>
              <div className="kycSectionHint">
                Upload identification documents now, or choose to provide them later
              </div>
            </div>

            <div className="kycSectionBody">
              <div className="kycEmbeddedDocs">
                <div className="kycEmbeddedDocsPrompt">
                  <button
                    type="button"
                    className={`kycEmbeddedDocsChoice ${
                      embeddedDocChoice === "upload_now" ? "is-selected" : ""
                    }`}
                    onClick={() => setEmbeddedDocChoice("upload_now")}
                  >
                    <span className="kycEmbeddedDocsChoiceTitle">Upload now</span>
                    <span className="kycEmbeddedDocsChoiceSub">
                      Add the identification documents before submitting the KYC form.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`kycEmbeddedDocsChoice ${
                      embeddedDocChoice === "later" ? "is-selected" : ""
                    }`}
                    onClick={() => setEmbeddedDocChoice("later")}
                  >
                    <span className="kycEmbeddedDocsChoiceTitle">Provide later</span>
                    <span className="kycEmbeddedDocsChoiceSub">
                      Receive a separate secure upload link after submitting.
                    </span>
                  </button>
                </div>

                {embeddedDocAttempted && !embeddedDocChoice ? (
                  <div className="kycInlineFieldError kycInlineFieldError--centered">
                    Choose one option to continue
                  </div>
                ) : null}

                <div className="kycEmbeddedDocsAmlNote">
                  Additional supporting documents related to your profile may also be requested at a later stage as part of our standard compliance process, in line with AML regulations.
                </div>

                {embeddedDocError ? (
                  <div className="alert alert-warning">{embeddedDocError}</div>
                ) : null}

                {embeddedDocChoice === "upload_now" ? (
                  <div className={`docUploadPartyList kycEmbeddedDocsList${embeddedGroupedRequirements.length === 1 ? " kycEmbeddedDocsList--single" : ""}`}>
                    {embeddedGroupedRequirements.map((group) => {
                      const isParty =
                        group.kind === "individual" || group.kind === "entity";
                      const partyReq = embeddedDocRequirements.find(
                        (req) => req?.meta?.kind === "individual"
                      );
                      const firstReq = group.requirements?.[0] || null;
                      const headerName =
                        group.partyName ||
                        partyReq?.meta?.partyName ||
                        clientName ||
                        "Client";
                      const headerKind = isParty
                        ? getPartyPill(group.kind) || "Individual"
                        : "Individual";
                      const headerRole =
                        group.partyRole || partyReq?.meta?.partyRole || "Client";
                      const categoryLine = isParty
                        ? firstReq?.section || "Identification documents"
                        : `Source of Wealth • ${
                            firstReq?.title
                              ? stripMetaInTitle(firstReq.title)
                              : "Requirements"
                          }`;

                      return (
                        <div
                          key={group.key}
                          className={[
                            "docUploadPartyCard",
                            isParty ? "isParty" : "isSupportingDocs",
                            group.kind === "individual" ? "isIndividual" : "",
                            group.kind === "entity" ? "isEntity" : "",
                            "kycEmbeddedDocsCard",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <div className="docUploadPartyHeader">
                            <div className="docUploadPartyHeaderLeft">
                              <div className="docUploadPartyAvatar">
                                <img
                                  src={getPartyIcon(group.kind)}
                                  alt=""
                                  aria-hidden="true"
                                />
                              </div>
                              <div className="docUploadPartyHeaderText">
                                <div className="docUploadPartyNameRow">
                                  <div className="docUploadPartyName">
                                    {headerName}
                                  </div>
                                  <span className={[
                                    "docUploadPill",
                                    group.kind === "individual" ? "isIndividual" : "",
                                    group.kind === "entity" ? "isEntity" : "",
                                  ].filter(Boolean).join(" ")}>
                                    {headerKind} - {headerRole}
                                  </span>
                                </div>
                                <div className="docUploadPartySub">
                                  {categoryLine}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="docUploadPartyBody">
                            {group.requirements.map((req) => {
                              const cardId = embeddedSafeStr(req.cardId);
                              const cardItems = embeddedDocItems.filter(
                                (item) => embeddedSafeStr(item.cardId) === cardId
                              );
                              const lines = parseRichLines(req.description);

                              return (
                                <div key={cardId} className="docUploadThreeCardRow">
                                  <div className="docUploadTwoCardsTop">
                                    <div className="docUploadReqCard">
                                      <div className="docUploadReqBullets">
                                        {req?.meta?.kind === "individual" &&
                                        !lines.some((line) => line.type === "heading") ? (
                                          <div className="docUploadReqHeading">
                                            {lines.filter(l => l.type === "bullet").length === 1
                                              ? "Provide the following:"
                                              : "Provide all of the following:"}
                                          </div>
                                        ) : null}

                                        {requirementLinesForDisplay(req, lines).map((line, idx) => {
                                          if (line.type === "heading") {
                                            const displayText = displayRequirementHeading(
                                              req,
                                              lines,
                                              line.text,
                                              line._displayIndex ?? idx
                                            );
                                            return (
                                              <div
                                                key={`${cardId}_h_${idx}`}
                                                className="docUploadReqHeading"
                                              >
                                                {displayText}
                                              </div>
                                            );
                                          }

                                          const flag = getLineFlag({
                                            req,
                                            line: line.text,
                                          });

                                          return (
                                            <div
                                              key={`${cardId}_b_${idx}`}
                                              className={
                                                line.type === "note"
                                                  ? `docUploadReqNote${
                                                      isConditionalEntityRequirement(req, lines)
                                                        ? " docUploadReqNote--leftAligned"
                                                        : ""
                                                    }`
                                                  : "docUploadReqBullet"
                                              }
                                            >
                                              {line.type === "note" ? null : (
                                                <span className="docUploadReqBulletDot">
                                                  •
                                                </span>
                                              )}
                                              <span className="docUploadReqBulletText">
                                                {line.type === "note"
                                                  ? renderRequirementNoteText(req, lines, line.text)
                                                  : line.text}
                                              </span>
                                              {flag ? (
                                                <span
                                                  className="docUploadFlag"
                                                  title="Country"
                                                >
                                                  {flag}
                                                </span>
                                              ) : null}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    <div className="docUploadUploadCard">
                                      <div
                                        className={[
                                          "docUploadDropZone docUploadDropZoneStack",
                                          embeddedDocDragOverCardId === cardId
                                            ? "isDragOver"
                                            : "",
                                          embeddedDocBusy ? "isDisabled" : "",
                                        ].join(" ")}
                                        onDrop={(event) =>
                                          onEmbeddedDocDrop(event, cardId)
                                        }
                                        onDragOver={(event) =>
                                          onEmbeddedDocDragOver(event, cardId)
                                        }
                                        onDragLeave={onEmbeddedDocDragLeave}
                                        onClick={() => openEmbeddedDocPicker(cardId)}
                                        role="button"
                                        tabIndex={0}
                                      >
                                        <div className="docUploadDropTopRow">
                                          <div className="docUploadDropIcon">
                                            <i className="bi bi-cloud-arrow-up" />
                                          </div>
                                          <div className="docUploadDropText">
                                            <div className="docUploadDropTitle">
                                              {cardItems.length ? "Add another file" : "Drop files here"}
                                            </div>
                                            <div className="docUploadDropSub">
                                              or click to browse
                                            </div>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn docUploadBtnOutline"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openEmbeddedDocPicker(cardId);
                                          }}
                                          disabled={embeddedDocBusy}
                                        >
                                          Browse
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {cardItems.length > 0 ? (
                                    <div className="docUploadFilesCard">
                                      <div className="docUploadFilesTitle">
                                        Uploaded documents
                                      </div>
                                      <div className="docUploadListWrap">
                                        {cardItems.map((item, idx) => (
                                          <div
                                            key={item.id}
                                            className={[
                                              "docUploadRow",
                                              idx === 0 ? "isFirst" : "",
                                              item.status === "error" ? "isError" : "",
                                            ].join(" ")}
                                          >
                                            <div className="docUploadRowIcon">
                                              {item.status === "done" ? (
                                                <i className="bi bi-check-circle-fill" />
                                              ) : item.status === "error" ? (
                                                <i className="bi bi-exclamation-triangle-fill" />
                                              ) : item.status === "uploading" ? (
                                                <i className="bi bi-arrow-repeat" />
                                              ) : (
                                                <i className="bi bi-file-earmark" />
                                              )}
                                            </div>
                                            <div className="docUploadRowMain">
                                              <div className="docUploadRowName">
                                                {item.name}
                                              </div>
                                              <div className="docUploadRowMeta">
                                                {formatEmbeddedBytes(item.size)}
                                                {item.error ? ` • ${item.error}` : ""}
                                              </div>
                                              {(item.status === "uploading" ||
                                                item.status === "done") ? (
                                                <div className="docUploadProgress">
                                                  <div
                                                    className="docUploadProgressBar"
                                                    style={{
                                                      width: `${Math.max(0, Math.min(100, item.progress || 0))}%`,
                                                    }}
                                                  />
                                                </div>
                                              ) : null}
                                            </div>
                                            <div className="docUploadRowRight">
                                              <div className="docUploadPct">
                                                {item.status === "done"
                                                  ? "100%"
                                                  : item.status === "uploading"
                                                  ? `${item.progress || 0}%`
                                                  : ""}
                                              </div>
                                              <button
                                                type="button"
                                                className="btn docUploadBtnDelete"
                                                onClick={() => removeEmbeddedDocItem(item.id)}
                                                disabled={
                                                  embeddedDocBusy ||
                                                  item.status === "uploading"
                                                }
                                                title="Remove"
                                              >
                                                <i className="bi bi-trash3" />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* COMMENTS FROM CLIENT TO REVIEW STAFF  */}
        <div className="card kycCard mb-3">
          <div className="kycSectionHead">
            <h5 className="kycSectionTitle">
              {sectionNo.comments} | Comments (optional)
            </h5>
            <div className="kycSectionHint">
              Use this space to clarify any answers or mention anything you left
              blank
            </div>
          </div>

          <div className="kycSectionBody">
            <textarea
              className="form-control"
              rows={4}
              value={form?.clientComment || ""}
              onChange={(e) => setAt(["clientComment"], e.target.value)}
              // disabled={saving}
            />
          </div>
        </div>

        </div>

        {/* ACTIONS */}
        <div className="d-flex gap-2 flex-wrap mt-3 kycClientBottomActions">
          <button
            className="btn kycBtnOutline"
            type="button"
            disabled={saving}
            onClick={handleSaveDraft}
          >
            {saving ? "Saving…" : "Save & continue later"}
          </button>

          <button
            className={`btn kycBtnPrimary ${
              !isDesktopSubmitComplete ? "kycBtnPrimary--blocked" : ""
            }`}
            type="button"
            disabled={saving}
            aria-disabled={!isDesktopSubmitComplete ? "true" : undefined}
            onClick={handleSubmit}
          >
            {saving ? "Submitting…" : "Submit"}
          </button>
        </div>
        </div>
      </div>

      <Toast ref={toastRef} position="top-center" className="kycDesktopToast" />
        </>
      )}
    </motion.div>
    </OwnershipLabelsProvider>
  );
}
